import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/shared/ui/primitives'
import { Icon } from '@/app/icons'
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  notificationKeys,
  type NotificationEntry,
  type UnreadCount,
} from './api'
import { NotificationItem } from './NotificationItem'

/** How often an open tab asks whether anything new arrived. */
const POLL_MS = 60_000

const PANEL_SIZE = 15

/**
 * The bell.
 *
 * <h2>Polling, not push</h2>
 * One indexed COUNT per minute per open tab, and only while the tab is
 * focused. Approvals move at human pace -- minutes, not milliseconds -- so a
 * socket held open for every user all day would buy latency nobody asked for
 * at a cost in moving parts that someone has to keep alive at 2am. If real
 * time ever becomes a genuine requirement, the answer is SSE on this same
 * endpoint shape, not a broker.
 *
 * <h2>The bell does not replace the email</h2>
 * Both are written from the same call in the same transaction. An approver who
 * has not opened the portal today still has to be told it is their turn, and
 * only mail can do that.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const unread = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: fetchUnreadCount,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  })

  // The rows are only fetched once somebody opens the panel: the badge is the
  // part that has to be current, and it is one COUNT rather than a page of
  // text pulled every minute for nobody to read.
  const list = useQuery({
    queryKey: notificationKeys.list({ size: PANEL_SIZE }),
    queryFn: () => fetchNotifications({ size: PANEL_SIZE }),
    enabled: open,
  })

  function applyCount(count: UnreadCount) {
    queryClient.setQueryData(notificationKeys.unread, count)
    void queryClient.invalidateQueries({ queryKey: notificationKeys.lists })
  }

  const markRead = useMutation({ mutationFn: markNotificationRead, onSuccess: applyCount })
  const markAll = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: applyCount })

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Focus goes back to the button that opened the panel, or a keyboard
      // user is dropped at the top of the document with no idea where they are.
      buttonRef.current?.focus()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function onSelect(entry: NotificationEntry) {
    if (!entry.read) markRead.mutate(entry.id)
    setOpen(false)
    if (entry.linkPath) navigate(entry.linkPath)
  }

  const count = unread.data?.unread ?? 0
  const entries = list.data?.content ?? []

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={count > 0 ? `Notifikasi, ${count} belum dibaca` : 'Notifikasi'}
        className={cn(
          'relative grid size-10 place-items-center rounded-md text-gray-600 transition-colors',
          'hover:bg-gray-100 hover:text-gray-800',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          open && 'bg-gray-100 text-gray-800',
        )}
      >
        <Icon name="bell" className="size-5" />
        {count > 0 && (
          // The number is capped, not the truth: "99" and "312" read the same
          // at this size, and both mean "go and look".
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-4 text-white"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifikasi"
          className={cn(
            'absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden',
            'rounded-lg bg-card shadow-card-lg animate-rise',
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <span className="font-semibold text-gray-900">Notifikasi</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-sm text-primary hover:underline disabled:opacity-60"
              >
                Tandai semua dibaca
              </button>
            )}
          </div>

          <div className="max-h-[min(26rem,60vh)] overflow-y-auto">
            {list.isLoading && <p className="px-4 py-6 text-gray-500">Memuat…</p>}

            {list.isError && (
              <p className="px-4 py-6 text-gray-500">Notifikasi tidak dapat dimuat.</p>
            )}

            {list.isSuccess && entries.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-500">Belum ada notifikasi.</p>
            )}

            <ul className="divide-y divide-line">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <NotificationItem entry={entry} onSelect={onSelect} />
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-line px-4 py-2.5 text-center">
            <Link
              to="/billing/notifications"
              onClick={() => setOpen(false)}
              className="text-sm text-primary hover:underline"
            >
              Lihat semua notifikasi
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
